import addOnStore from 'Models/AddOnStore';
import { appConfigStore } from 'Models/AppConfigStore';
import { VisibilityStatus } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizard';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { TabSelected } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import { BookingAddOn } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BookingAddOn';

export function getFerryAndRouteIds(trips: SelectedTrips, mode: 'departing' | 'returning' | 'all' = 'all'): string[] {
	const ids: (string | undefined)[] = [];
	if (mode === 'departing' || mode === 'all') {
		ids.push(trips.departingTrip?.routeId);
		ids.push(trips.departingTrip?.ferryId);
	}
	if (mode === 'returning' || mode === 'all') {
		ids.push(trips.returningTrip?.routeId);
		ids.push(trips.returningTrip?.ferryId);
	}

	return ids.filter(x => x !== undefined) as string[];
}

/**
 * The add-ons that survive a route change, given the selection before it.
 *
 * Add-on sections are scoped to routes and ferries (`RoutesAdditionalBookingSections`), so an
 * option carried across to a different route may not be offered there at all. What decides whether
 * an add-on survives is therefore whether its SECTION covers the new journey:
 *
 * - **Still offered on the new journey** — retained at the quantity already paid for. Dropping it
 *   refunded an extra the customer keeps: the same trolley or kayak is sold on the leg they are
 *   moving to, so there is nothing to re-pick and no reason to credit it back. They can still
 *   change the quantity on the add-ons step like any other alteration.
 * - **Not offered on the new journey** — cleared, because there is no counter to show it on and no
 *   price to charge it at. `ApplyAddOnsUseCase.GetApplicableOptionIdsAsync` reaches the same verdict
 *   server-side from the trips' real route/ferry ids, which is the authoritative check.
 * - **Non-removable** — retained either way. That flag means non-refundable (see
 *   `FerryBookingSummariesService.CalculateSummaryPrices`, which subtracts it from `maxRefundPrice`
 *   on cancellation), so clearing it would refund something the customer is not entitled to have
 *   back. `ApplyAddOnsUseCase` enforces the same floor server-side.
 *
 * `routeIdsOnNewJourney` must be the UNION of both legs' routes, not the leg being mapped. The
 * picker builds its sections from `getFerryAndRouteIds(selectedTrips, 'all')` and offers each one on
 * both legs, and routes are directional — a return pair's legs never share a RouteId — so a per-leg
 * check would clear every single-route add-on on the other leg. Ferry ids are deliberately absent:
 * the trip is re-picked after a route change, so the new ferry isn't known yet. A ferry-only-scoped
 * section therefore doesn't match and is cleared, which is what happened to every add-on before.
 *
 * Only applies while editing a booked booking: in CREATE mode nothing has been paid, so there is
 * nothing to preserve and the user re-picks from scratch.
 */
export function addOnsRetainedOnRouteChange(
	previousAddOns: BookingAddOn[] | undefined,
	wizardMode: BookingWizardData['wizardMode'],
	routeIdsOnNewJourney: string[],
): BookingAddOn[] {
	if (wizardMode !== 'ALTERATION') {
		return [];
	}

	const sectionIdsOnNewJourney = new Set(
		addOnStore.getAddOnSectionsByFerriesAndRoutes(routeIdsOnNewJourney).map(section => section.id),
	);

	return (previousAddOns ?? []).filter(option => {
		if (option.amount <= 0) {
			return false;
		}

		const addOn = addOnStore.findAddOn(option.optionId);

		// Non-refundable: kept regardless of where the booking is going.
		if (addOn?.removable === false) {
			return true;
		}

		// An add-on the picker hides on an alteration has no counter to carry it, so retaining it
		// would leave an amount on the booking with nothing on screen explaining it. Mirrors
		// `useFilterAddOnSections`, which excludes these outright in ALTERATION mode.
		if (addOn === null || addOn.isInitialBookingOnly === true) {
			return false;
		}

		const section = addOnStore.findAddOnSection(option.optionId);
		return section !== null && sectionIdsOnNewJourney.has(section.id);
	});
}

/**
 * Collects add-on option IDs that existed on the original booking (from oldWizardData).
 * Returns undefined for new bookings or when oldWizardData is not available.
 */
export function getExistingAddOnIds(
	wizardData: BookingWizardData,
	oldWizardData?: BookingWizardData | null,
): string[] | undefined {
	if (wizardData.wizardMode !== 'ALTERATION' || !oldWizardData) {
		return undefined;
	}
	const ids: string[] = [];
	for (const opt of oldWizardData.innerWizardData.departingTripOptions ?? []) {
		if (opt.amount > 0) ids.push(opt.optionId);
	}
	for (const opt of oldWizardData.innerWizardData.returningTripOptions ?? []) {
		if (opt.amount > 0 && !ids.includes(opt.optionId)) ids.push(opt.optionId);
	}
	return ids;
}

export function getAddOnsTabVisibility(
	tabSelected: TabSelected,
	trips: SelectedTrips,
	existingAddOnIds?: string[],
): VisibilityStatus {
	// If the user has navigated directly to the add-ons page (e.g. editing add-ons from booking
	// detail view), keep the tab visible even if the add-on store hasn't loaded yet
	const paths = window.location.pathname.split('/');
	if (paths.length !== 0 && paths[paths.length - 1] === 'add-ons') {
		return VisibilityStatus.VISIBLE;
	}

	if (!addOnStore.hasPassengerAddOns(trips, existingAddOnIds) && tabSelected === 'passenger') {
		if (appConfigStore.passengerNoteEnabled) {
			// Even if there are no passenger add-ons, we need the Add-ons tab for additional details textbox
			return VisibilityStatus.VISIBLE;
		}
		return VisibilityStatus.EXCLUDED;
	}

	if (!addOnStore.hasVehicleAddOns(trips, existingAddOnIds) && tabSelected === 'vehicle') {
		return VisibilityStatus.EXCLUDED;
	}

	if (!addOnStore.hasFreightAddOns(trips, existingAddOnIds) && tabSelected === 'freight') {
		return VisibilityStatus.EXCLUDED;
	}

	// Only show Add-ons tab if the trip the user has selected has additional booking options available

	if (trips.departingTrip === undefined && trips.returningTrip === undefined) {
		return VisibilityStatus.EXCLUDED;
	}

	const ids = getFerryAndRouteIds(trips);

	if (ids.length === 0) {
		return VisibilityStatus.EXCLUDED;
	}

	// Check if any add-on sections match the selected trips.
	// getAddOnSectionsByFerriesAndRoutes handles sections with no ferries/routes
	// assigned (which apply to all ferries/routes).
	const matchingSections = addOnStore.getAddOnSectionsByFerriesAndRoutes(ids);
	if (matchingSections.length > 0) {
		return VisibilityStatus.VISIBLE;
	}

	return VisibilityStatus.EXCLUDED;
}
